import React, { useState, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
  Box,
  Grid,
  Typography,
  Card,
  CircularProgress,
} from "@material-ui/core"
import CheckIcon from "@material-ui/icons/Check"
import {
  toggleSendBillingDialog,
  toggleDigitalSignatureDialog,
  toggleBillingPaymentDialog,
} from "../../../../actions/billingDialogs"
import { updateClientEstimateInvoiceStatus } from "../../../../actions/billing-crew-client"
import { openSnackBar } from "../../../../actions/snack-bar"
import moment from "moment"
import formatNumberWithCommas from "../../../../utils/formatNumberWithCommas"
import calculateTotals from "../../../../utils/calculateTotals"
import classNames from "classnames"
import PropTypes from "prop-types"

import styles from "./styles"
import GreenButton from "../../Common/GreenButton/index"
import SendLog from "../SendLog/index"
import LoadingButton from "../../../LoadingButton/index"

const ClientEstimateRightActions = ({
  dealMemoData = {},
  setDealMemoData = () => {},
}) => {
  const classes = styles()
  const dispatch = useDispatch()

  const [approveWorking, toggleApproveWorking] = useState(false)
  const [reportWorking, toggleReportWorking] = useState(false)
  const discount = (dealMemoData && dealMemoData.discount) || 0

  //display controllers
  const estimateAccepted =
    dealMemoData && Boolean(dealMemoData.estimate_accepted)
  const depositRequired = dealMemoData && Boolean(dealMemoData.deposit_required)
  // debugger;
  const depositPaidAmount =
    depositRequired && Boolean(dealMemoData.first_paid)
      ? dealMemoData.first_half_amount
      : 0
  const changePaymentMethodIfCheck =
    dealMemoData.deposit_required === 1 &&
    (dealMemoData.first_payment_type === "ach" ||
      dealMemoData.first_payment_type === "check")

  const companyLogo =
    dealMemoData &&
    dealMemoData.company_logo_image &&
    JSON.parse(dealMemoData.company_logo_image).url

  const amounts = useMemo(
    () => {
      if (dealMemoData && dealMemoData.users) {
        let crewArr = [...dealMemoData.roles, ...dealMemoData.users]
        return calculateTotals(crewArr, depositPaidAmount, discount)
      }
    },
    [dealMemoData, depositPaidAmount]
  )
  // console.log('amounts', amounts);

  // *** APPROVE ESTIMATE  ***

  const handleApproveEstimate = dialogVals => {
    toggleApproveWorking(true)
    let actionData = {
      url_type: "accepted",
      type: "estimate",
      client_estimate_signature: dialogVals.fullName,
      client_estimate_terms_accepted: Number(dialogVals.termsAccepted),
    }
    dispatch(
      updateClientEstimateInvoiceStatus(actionData, dealMemoData.shoot_id)
    )
      .then(res => {
        toggleApproveWorking(false)
        setDealMemoData(oldState => {
          return {
            ...oldState,
            estimate_accepted: 1,
            client_estimate_signature: actionData.client_estimate_signature,
            client_estimate_terms_accepted: 1,
            estimate_accepted_date_time: moment().format(),
          }
        })
        dispatch(
          openSnackBar({ message: "Submitted estimate approval successfully" })
        )
      })
      .catch(err => {
        console.log(err)
        toggleApproveWorking(false)
        dispatch(openSnackBar({ message: "Error approving estimate" }))
      })
  }

  const openSignatureDialog = () => {
    let dialogData = {
      headerLogo: companyLogo,
      acceptFunc: handleApproveEstimate,
    }
    dispatch(toggleDigitalSignatureDialog(true, dialogData))
  }

  // *** APPROVE ESTIMATE END ***

  // *** REPORT ESTIMATE  ***

  const handleReportEstimateIssue = dialogVals => {
    toggleReportWorking(true)
    const request = {
      url_type: "report",
      email_to: dialogVals.recipients.join(", "),
      subject: dialogVals.subject,
      message: dialogVals.message,
    }
    dispatch(updateClientEstimateInvoiceStatus(request, dealMemoData.shoot_id))
      .then(() => {
        toggleReportWorking(false)
        dispatch(
          openSnackBar({
            message: `Reported issue details to ${
              dealMemoData.company_name
            } staff`,
          })
        )
      })
      .catch(err => {
        console.log(err)
        toggleReportWorking(false)
        dispatch(openSnackBar({ message: "Error sending issue details" }))
      })
  }

  const openReportIssue = () => {
    let defaultReportEmail =
      dealMemoData.representative_email || dealMemoData.creator_email
        ? (dealMemoData.representative_email || dealMemoData.creator_email)
            .split(",")
            .map(x => x.trim())
        : null
    let dialogData = {
      type: "Issue",
      shootName: dealMemoData.shoot_name,
      defaultEmail: defaultReportEmail,
      sendFunc: handleReportEstimateIssue,
    }
    dispatch(toggleSendBillingDialog(true, dialogData))
  }
  // *** REPORT ESTIMATE END ***

  const openPaymentDialog = () => {
    let data = {
      final: false,
      amountDue: amounts.half,
      totalAmount: amounts.original - discount,
      headerLogo: companyLogo,
      generalData: dealMemoData,
      setGeneralData: setDealMemoData,
    }

    dispatch(toggleBillingPaymentDialog(true, data))
  }

  return (
    <Grid align="center">
      <SendLog data={dealMemoData} />
      {!estimateAccepted ? (
        <Box
          mt={4}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Typography variant="h3">Digital Signage & Agreement</Typography>
          <Typography
            align="left"
            variant="subtitle1"
            style={{
              marginBottom: 10,
              marginTop: 10,
            }}
          >
            Please sign off on this estimate
          </Typography>
          <Box alignSelf="center" width="80%" mt={4}>
            <GreenButton
              variant="contained"
              fullWidth
              onClick={openSignatureDialog}
              disabled={approveWorking}
            >
              {approveWorking ? (
                <CircularProgress size={24} style={{ color: "#fff" }} />
              ) : (
                "Approve This Estimate"
              )}
            </GreenButton>
          </Box>
        </Box>
      ) : (
        <Box mb={1}>
          {/* Digital Sigantrue Card */}
          <Card classes={{ root: classes.card }}>
            <Box className={classes.rowWrapper}>
              <Typography variant="h3">Digital Signature</Typography>
              <Box
                className={classNames(classes.statusDot, {
                  [classes.acceptedDot]: estimateAccepted,
                })}
              >
                <CheckIcon className={classes.checkIcon} />
              </Box>
            </Box>
            <Typography
              align="left"
              variant="subtitle1"
              style={{
                marginBottom: 10,
                marginTop: 10,
              }}
            >
              Signed on{" "}
              {moment(dealMemoData.estimate_accepted_date_time).format(
                "M/D/YY[ @ ]h:mm A"
              )}{" "}
              by {dealMemoData.client_estimate_signature}
            </Typography>
          </Card>
          {/* Status Card - only display this card if need to make a deposit / made a deposit */}
          {depositRequired && (
            <Card
              classes={{ root: classes.card }}
              style={{ marginTop: "20px" }}
            >
              <Box className={classes.rowWrapper}>
                <Typography variant="h3">
                  {depositPaidAmount > 0
                    ? "Payment Sent"
                    : "Partial Payment Due"}
                </Typography>
                <Box
                  className={classNames(classes.statusDot, {
                    [classes.acceptedDot]: depositPaidAmount > 0,
                  })}
                >
                  {depositPaidAmount > 0 && (
                    <CheckIcon className={classes.checkIcon} />
                  )}
                </Box>
              </Box>
              <Box mt={2} mb={2}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    textAlign: "left",
                    marginBottom: 5,
                  }}
                >
                  {depositPaidAmount > 0
                    ? "Partial payment sent:"
                    : "Partial payment request for:"}
                </Typography>
                <Box className={classes.rowWrapper}>
                  <Typography className={classes.greenFont}>
                    50% of estimate
                  </Typography>
                  {depositPaidAmount > 0 ? (
                    <Typography className={classes.greenFont}>
                      ${formatNumberWithCommas(depositPaidAmount)}
                    </Typography>
                  ) : (
                    <Typography className={classes.greenFont}>
                      ${amounts && formatNumberWithCommas(amounts.half)}
                    </Typography>
                  )}
                </Box>
                {!dealMemoData.first_paid && (
                  <Box display="flex" justifyContent="center" mt={4}>
                    <GreenButton variant="outlined" onClick={openPaymentDialog}>
                      {changePaymentMethodIfCheck
                        ? "Change Payment Method"
                        : "Submit Payment"}
                    </GreenButton>
                  </Box>
                )}
              </Box>
            </Card>
          )}
        </Box>
      )}
      <Box alignSelf="center" width="80%" mt={2}>
        <LoadingButton
          fullWidth
          variant="outlined"
          className={classes.redButton}
          onClick={openReportIssue}
          disabled={reportWorking}
        >
          {reportWorking ? (
            <CircularProgress
              size={20}
              color="inherit"
              style={{ margin: "4px 0" }}
            />
          ) : (
            "Report an Issue"
          )}
        </LoadingButton>
      </Box>
    </Grid>
  )
}

ClientEstimateRightActions.propTypes = {
  dealMemoData: PropTypes.object,
  setDealMemoData: PropTypes.func,
}

export default ClientEstimateRightActions
